<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ChannelCard from './channel/ChannelCard.vue';
import ChannelInfoModal from './channel/ChannelInfoModal.vue';
import WebWidgetConnectModal from './channel/WebWidgetConnectModal.vue';
import { clearOnboardingKbSession } from '../../../../helper/onboardingKbSession';
import {
  CHANNEL_TYPES,
  isChannelTypeActive,
} from 'dashboard/constants/channelTypes';

export default {
  name: 'OnboardingChannelStep',
  components: {
    ChannelCard,
    ChannelInfoModal,
    WebWidgetConnectModal,
  },
  data() {
    return {
      activeInfoModalChannel: null,
      showWebWidgetModal: false,
      connectedChannels: {},
      // Whether ANY of the account's inboxes has a chatbot behind it — not
      // just whether an inbox exists. A channel with no chatbot isn't
      // actually usable for this product, so it shouldn't count as
      // "connected" for the purposes of finishing onboarding.
      hasChatbot: false,
      isFinishing: false,
    };
  },
  computed: {
    ...mapGetters('inboxes', ['getInboxes']),
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    accountId() {
      return this.$route.params.accountId;
    },
    // Same source ChannelList.vue (settings) uses for its feature gating —
    // keeps onboarding's "which channels are usable" answer identical to
    // settings' for the same account.
    accountFeatures() {
      return (
        this.$store.getters['accounts/getAccount'](this.accountId)?.features ||
        {}
      );
    },
    // Website keeps its own special-cased card + WebWidgetConnectModal
    // (auto-creates a chatbot); every other type reuses the generic
    // ChannelInfoModal handoff to the standard settings flow.
    nonWebsiteChannelTypes() {
      return CHANNEL_TYPES.filter(ct => ct.key !== 'website');
    },
    activeInfoModal() {
      return this.activeInfoModalChannel
        ? CHANNEL_TYPES.find(ct => ct.key === this.activeInfoModalChannel)
        : null;
    },
    // The real signal, not local connectedChannels — the OAuth-handoff
    // channels navigate away to the standard settings inbox-creation flow
    // entirely, outside this component, so there's no event or callback
    // that could ever set connectedChannels for them. Checking the
    // account's actual inbox list is the only way to know a channel was
    // connected regardless of which one was used. Also requires
    // hasChatbot — an inbox alone isn't enough (see below).
    hasConnectedChannel() {
      return this.getInboxes.length > 0 && this.hasChatbot;
    },
  },
  async mounted() {
    // Refresh in case the user just came back from the OAuth channel
    // flow (settings_inboxes_page_channel), which lives outside this
    // component entirely and can't push updates back to it directly —
    // including whether a chatbot ended up attached there too.
    await this.refreshConnectionStatus();
    // Only one inbox is ever needed to finish onboarding — if a channel is
    // already connected by the time this step is reached (e.g. the user
    // navigated back here after already finishing), don't leave them
    // sitting on the channel grid, push them straight through.
    if (this.hasConnectedChannel) {
      this.finishOnboarding();
    }
  },
  methods: {
    ...mapActions('onboarding', ['markMilestone']),
    ...mapActions('inboxes', { fetchInboxes: 'get' }),
    ...mapActions('chatbotConfig', ['checkChatbotExists']),
    async refreshConnectionStatus() {
      await this.fetchInboxes();
      this.hasChatbot = await this.anyInboxHasChatbot();
    },
    async anyInboxHasChatbot() {
      if (!this.getInboxes.length) return false;
      const results = await Promise.all(
        this.getInboxes.map(inbox =>
          this.checkChatbotExists({ inboxId: inbox.id })
        )
      );
      return results.some(Boolean);
    },
    isChannelActive(channelType) {
      return isChannelTypeActive(channelType, this.accountFeatures);
    },
    // Shared by both the card grid and the info modal so every channel
    // type resolves its display name the same way (with the account's
    // white-label API channel name taking precedence when set).
    channelDisplayName(key) {
      if (key === 'api' && this.globalConfig.apiChannelName) {
        return this.globalConfig.apiChannelName;
      }
      return this.$t(`ONBOARDING_SHELL.STEPS.CHANNEL.${key.toUpperCase()}`);
    },
    openInfoModal(channelKey) {
      this.activeInfoModalChannel = channelKey;
    },
    closeInfoModal() {
      this.activeInfoModalChannel = null;
    },
    openWebWidgetModal() {
      this.showWebWidgetModal = true;
    },
    closeWebWidgetModal() {
      this.showWebWidgetModal = false;
      // Only auto-advance once the widget's own chatbot-creation step has
      // actually succeeded — closing early (the X button, or dismissing
      // the entry form) must not push the user forward.
      if (this.hasConnectedChannel) {
        this.finishOnboarding();
      }
    },
    handleWebWidgetConnected() {
      this.$set(this.connectedChannels, 'website', true);
      // The @connected event only fires once WebWidgetConnectModal's own
      // chatbot-creation step has already succeeded, so this is known-true
      // here without another round trip.
      this.hasChatbot = true;
    },
    handleContinue() {
      // Step is not skippable — at least one real inbox must exist for
      // this account before finishing (see hasConnectedChannel).
      if (!this.hasConnectedChannel || this.isFinishing) return;
      this.finishOnboarding();
    },
    // Only one inbox is ever needed to finish onboarding, so every path
    // that ends in a connected channel (the in-modal Website flow, or
    // discovering an already-connected channel on mount) funnels through
    // here rather than leaving the user on the channel grid to click
    // Continue themselves.
    async finishOnboarding() {
      if (this.isFinishing) return;

      this.isFinishing = true;
      try {
        // markMilestone swallows its own errors and resolves to null on
        // failure — check that explicitly. Navigating on a failed call
        // would show the congrats page and dashboard as if onboarding
        // finished while channel_connected was never actually persisted,
        // only to bounce the user back into the wizard on their next
        // refresh with no explanation.
        const result = await this.markMilestone('channel_connected');
        if (!result) {
          useAlert(this.$t('ONBOARDING_SHELL.STEPS.CHANNEL.MILESTONE_ERROR'));
          return;
        }
        // The knowledge base is done being built — forget the resume record.
        clearOnboardingKbSession(this.accountId);
        this.$router.push({
          name: 'onboarding_plan',
          params: { accountId: this.accountId },
        });
      } finally {
        this.isFinishing = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 md:p-10 w-full h-full">
    <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100">
      {{ $t('ONBOARDING_SHELL.STEPS.CHANNEL.HEADING') }}
    </h2>

    <div class="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4 content-start">
      <ChannelCard
        :name="$t('ONBOARDING_SHELL.STEPS.CHANNEL.WEB_WIDGET.TITLE')"
        icon-key="website"
        :connected="!!connectedChannels.website"
        @click="openWebWidgetModal"
      />
      <ChannelCard
        v-for="channelType in nonWebsiteChannelTypes"
        :key="channelType.key"
        :name="channelDisplayName(channelType.key)"
        :icon-key="channelType.iconKey"
        :connected="!!connectedChannels[channelType.key]"
        :inactive="!isChannelActive(channelType)"
        @click="openInfoModal(channelType.key)"
      />
    </div>

    <ChannelInfoModal
      v-if="activeInfoModal"
      :show="!!activeInfoModalChannel"
      :channel-name="channelDisplayName(activeInfoModalChannel)"
      :description="
        $t(
          `ONBOARDING_SHELL.STEPS.CHANNEL.${activeInfoModalChannel.toUpperCase()}_DESCRIPTION`
        )
      "
      :sub-page="activeInfoModal.subPage"
      @close="closeInfoModal"
    />

    <WebWidgetConnectModal
      :show="showWebWidgetModal"
      @close="closeWebWidgetModal"
      @connected="handleWebWidgetConnected"
    />

    <div class="flex justify-end items-center">
      <woot-button
        :disabled="!hasConnectedChannel || isFinishing"
        :is-loading="isFinishing"
        @click="handleContinue"
      >
        {{ $t('ONBOARDING_SHELL.CONTINUE_BUTTON') }}
      </woot-button>
    </div>
  </div>
</template>
